import { useMemo, useState } from 'react';
import { CalendarBlank, Receipt, Warning } from '@phosphor-icons/react';
import { EmptyState } from '@/components/layout/EmptyState';
import { RequestWaiverDialog } from '@/components/billing/RequestWaiverDialog';
import {
  useBillingMonths,
  useBillingStatement,
  type StatementLineItem,
  type BillingStatement,
} from '@/lib/billing/useBillingStatement';
import { useRefunds } from '@/lib/refunds/useRefunds';
import { cn } from '@/lib/utils/cn';

// Itemized, live-computed monthly statement. One line per confirmed
// interview/assessment setup (the billing unit — each setup is one new
// prompt generation); waived setups stay visible at $0 for transparency.
// All amounts come from the get_b2b_billing_statement RPC verbatim — the
// same numbers the F1Jobs admin portal bills from.

export function MonthlyStatement() {
  const {
    data: months,
    isLoading: monthsLoading,
    isError: monthsError,
    error: monthsErr,
  } = useBillingMonths();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Default to the newest month with activity; a brand-new tenant with no
  // setups still gets an (empty) current-month statement.
  const effectiveMonth = monthsLoading
    ? null
    : (selectedMonth ?? months?.[0]?.month ?? currentMonthKey());
  const {
    data: statement,
    isLoading: statementLoading,
    isError: statementError,
    error: statementErr,
  } = useBillingStatement(effectiveMonth);

  // Waiver requests ride the existing refund-request pipeline: a pending
  // request hides the line's "Request waiver" action; approval waives the
  // charge on the statement itself.
  const { data: refunds } = useRefunds();
  const pendingWaiverSessions = useMemo(
    () =>
      new Set(
        (refunds ?? [])
          .filter((r) => r.status === 'pending' && r.session_id)
          .map((r) => r.session_id as string)
      ),
    [refunds]
  );
  const [waiverItem, setWaiverItem] = useState<StatementLineItem | null>(null);

  const isLoading = monthsLoading || statementLoading;
  const isError = monthsError || statementError;
  const error = (monthsErr ?? statementErr) as Error | null;

  return (
    <div data-testid="monthly-statement" className="glass-panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-6 py-4">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-neurax-electric" />
          <h3 className="text-sm font-semibold text-white">Monthly statement</h3>
        </div>
        {(months?.length ?? 0) > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarBlank className="h-3.5 w-3.5" />
            <select
              data-testid="statement-month-picker"
              value={effectiveMonth ?? ''}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white outline-none transition focus:border-neurax-electric/40"
            >
              {months!.map((m) => (
                <option key={m.month} value={m.month} className="bg-neurax-ink text-white">
                  {monthLabel(m.month)} — {fmtAmount(m.total, m.currency, true)} ({m.billable_count}{' '}
                  billable, {m.waived_count} waived)
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isError && (
        <div
          role="alert"
          className="flex items-start gap-3 border-b border-white/5 px-6 py-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{error?.message || 'Failed to load the billing statement.'}</span>
        </div>
      )}

      {isLoading && (
        <div className="divide-y divide-white/5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              data-testid="statement-skeleton"
              className="grid grid-cols-12 items-center gap-4 px-6 py-4"
            >
              <div className="col-span-4 h-3 animate-pulse rounded bg-white/5" />
              <div className="col-span-4 h-3 animate-pulse rounded bg-white/5" />
              <div className="col-span-2 h-3 animate-pulse rounded bg-white/5" />
              <div className="col-span-2 h-3 animate-pulse rounded bg-white/5 ml-auto w-16" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && statement && statement.line_items.length === 0 && (
        <div data-testid="statement-empty" className="px-6 py-12">
          <EmptyState
            title="No setups this month"
            description="Interview and assessment setups will appear here as itemized charges."
          />
        </div>
      )}

      {!isLoading && !isError && statement && statement.line_items.length > 0 && (
        <>
          <div className="grid grid-cols-12 items-center gap-4 border-b border-white/5 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">
            <div className="col-span-5 sm:col-span-4">Candidate</div>
            <div className="hidden sm:col-span-3 sm:block">Position & company</div>
            <div className="col-span-3 sm:col-span-2">Type</div>
            <div className="hidden sm:col-span-1 sm:block">Setup</div>
            <div className="col-span-4 sm:col-span-2 text-right">Amount</div>
          </div>
          <div className="divide-y divide-white/5">
            {statement.line_items.map((item) => (
              <StatementRow
                key={item.session_id}
                item={item}
                currency={statement.pricing.currency}
                waiverPending={pendingWaiverSessions.has(item.session_id)}
                onRequestWaiver={setWaiverItem}
              />
            ))}
          </div>
          <StatementSummaryFooter statement={statement} />
        </>
      )}

      {waiverItem && statement && (
        <RequestWaiverDialog
          item={waiverItem}
          currency={statement.pricing.currency}
          onClose={() => setWaiverItem(null)}
        />
      )}
    </div>
  );
}

function StatementRow({
  item,
  currency,
  waiverPending,
  onRequestWaiver,
}: {
  item: StatementLineItem;
  currency: string;
  waiverPending: boolean;
  onRequestWaiver: (item: StatementLineItem) => void;
}) {
  return (
    <div
      data-testid={`statement-row-${item.session_id}`}
      className="grid grid-cols-12 items-center gap-4 px-6 py-4"
    >
      <div className="col-span-5 sm:col-span-4 min-w-0">
        <p
          className={cn(
            'truncate text-sm font-medium',
            item.waived ? 'text-muted-foreground' : 'text-white'
          )}
        >
          {item.end_client_name}
        </p>
        {item.interview_date && (
          <p className="truncate text-[11px] text-muted-foreground">
            Interview {fmtDate(item.interview_date)}
          </p>
        )}
      </div>
      <div className="hidden sm:col-span-3 sm:block min-w-0">
        <p className="truncate text-xs text-white/80">{item.position_title || '—'}</p>
        {item.company_name && (
          <p className="truncate text-[11px] text-muted-foreground">{item.company_name}</p>
        )}
      </div>
      <div className="col-span-3 sm:col-span-2 flex flex-wrap items-center gap-1.5">
        <TypePill type={item.session_type} />
        {item.waived && (
          <span
            data-testid="waived-badge"
            title={item.waive_reason ?? 'Not billable'}
            className="inline-flex items-center rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-neurax-amber"
          >
            Waived
          </span>
        )}
      </div>
      <div className="hidden sm:col-span-1 sm:block text-xs text-muted-foreground">
        {fmtDate(item.billed_on)}
      </div>
      <div className="col-span-4 sm:col-span-2 text-right">
        {item.waived ? (
          <p className="font-display text-sm text-muted-foreground">
            <span className="mr-1.5 text-xs line-through opacity-60">
              {fmtAmount(item.unit_price, currency, true)}
            </span>
            {fmtAmount(0, currency, true)}
          </p>
        ) : (
          <>
            <p className="font-display text-sm font-semibold text-white">
              {fmtAmount(item.amount, currency, true)}
            </p>
            {waiverPending ? (
              <span
                data-testid="waiver-pending-badge"
                className="mt-0.5 inline-flex items-center rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-neurax-amber"
              >
                Waiver requested
              </span>
            ) : (
              <button
                type="button"
                data-testid={`request-waiver-${item.session_id}`}
                onClick={() => onRequestWaiver(item)}
                className="mt-0.5 text-[11px] text-muted-foreground underline-offset-2 transition hover:text-neurax-amber hover:underline"
              >
                Request waiver
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TypePill({ type }: { type: string }) {
  const isAssessment = type === 'online_assessment';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        isAssessment
          ? 'border-neurax-plasma/30 bg-neurax-plasma/10 text-neurax-plasma'
          : 'border-neurax-electric/30 bg-neurax-electric/10 text-neurax-electric'
      )}
    >
      {isAssessment ? 'Assessment' : 'Interview'}
    </span>
  );
}

function StatementSummaryFooter({ statement }: { statement: BillingStatement }) {
  const { summary, pricing } = statement;
  const ccy = pricing.currency;
  return (
    <div className="border-t border-white/5 bg-white/[0.02] px-6 py-4">
      <div className="ml-auto flex max-w-sm flex-col gap-1.5 text-sm">
        {summary.interview_count > 0 && (
          <SummaryLine
            testId="statement-interviews-line"
            label={`Interviews — ${summary.billable_interviews} × ${fmtAmount(pricing.interview_price, ccy, true)}`}
            value={fmtAmount(summary.billable_interviews * pricing.interview_price, ccy, true)}
          />
        )}
        {summary.assessment_count > 0 && (
          <SummaryLine
            testId="statement-assessments-line"
            label={`Assessments — ${summary.billable_assessments} × ${fmtAmount(pricing.assessment_price, ccy, true)}`}
            value={fmtAmount(summary.billable_assessments * pricing.assessment_price, ccy, true)}
          />
        )}
        {summary.waived_count > 0 && (
          <SummaryLine
            testId="statement-waived-line"
            label={`Waived setups — ${summary.waived_count} × ${fmtAmount(0, ccy, true)}`}
            value={fmtAmount(0, ccy, true)}
            muted
          />
        )}
        <div className="my-1 border-t border-white/5" />
        <SummaryLine
          testId="statement-subtotal"
          label="Subtotal"
          value={fmtAmount(summary.subtotal, ccy, true)}
        />
        {summary.discount_pct > 0 && (
          <SummaryLine
            testId="statement-discount"
            label={`Volume discount (${summary.discount_pct}%)`}
            value={`−${fmtAmount(summary.discount_amount, ccy, true)}`}
            accent
          />
        )}
        <div data-testid="statement-total" className="flex items-baseline justify-between">
          <span className="font-semibold text-white">Total</span>
          <span className="font-display text-lg font-semibold text-white">
            {fmtAmount(summary.total, ccy, true)}
          </span>
        </div>
        {pricing.is_default_pricing && (
          <p
            data-testid="statement-default-pricing"
            className="mt-1 text-right text-[11px] text-muted-foreground"
          >
            Standard rates — no custom pricing configured for your plan yet.
          </p>
        )}
      </div>
    </div>
  );
}

function SummaryLine({
  testId,
  label,
  value,
  muted,
  accent,
}: {
  testId: string;
  label: string;
  value: string;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <div data-testid={testId} className="flex items-baseline justify-between gap-4">
      <span className={cn('text-xs', muted ? 'text-muted-foreground/70' : 'text-muted-foreground')}>
        {label}
      </span>
      <span
        className={cn(
          'font-display text-sm',
          accent ? 'text-neurax-mint' : muted ? 'text-muted-foreground' : 'text-white'
        )}
      >
        {value}
      </span>
    </div>
  );
}

function currentMonthKey(): string {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtAmount(n: number, ccy: string, showCents = false) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy || 'USD',
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    }).format(n);
  } catch {
    return showCents ? `$${n.toFixed(2)}` : `$${Math.round(n)}`;
  }
}
